import type { ReactNode } from 'react';
import type { Collector, StockPicking } from '@/types/odoo';
import type { DashboardFase } from '@/api/projectFases';
import { faseStatus, type FaseAggregate } from './dashboardFases';

/**
 * Pure summary-line builders for the collapsed section headers. They render only
 * over already-loaded, in-memory data (no fetch) and bold the key numbers so the
 * collapsed header still carries the section's headline figures. Returned as
 * ReactNode so a value can be emphasised; `<b>` inherits the header's muted
 * colour but bumps weight (matching the mockup's `.sum b`).
 */

const FASE_STATUS_LABEL = {
  gepland: 'gepland',
  in_uitvoering: 'in uitvoering',
  uitgevoerd: 'uitgevoerd',
} as const;

/** "yyyy-mm-dd" → "dd/mm". */
function dm(s: string): string {
  return `${s.slice(8, 10)}/${s.slice(5, 7)}`;
}

function fmtRange(range: { from: string; to: string } | null): string | null {
  if (!range) return null;
  return range.from === range.to ? dm(range.from) : `${dm(range.from)}–${dm(range.to)}`;
}

/** Planning header: fase name · date range · m² · derived status. */
export function planningSummary(
  fase: DashboardFase,
  aggregate: FaseAggregate,
  dateRange: { from: string; to: string } | null,
): ReactNode {
  const range = fmtRange(dateRange);
  const status = FASE_STATUS_LABEL[faseStatus(aggregate)];
  return (
    <>
      {fase.name}
      {range && <> · {range}</>}
      {' · '}
      <b className="font-extrabold text-td-navy">{Math.round(aggregate.totalM2)} m²</b>
      {' · '}
      {status}
    </>
  );
}

/** Count of collectors that are op druk. */
export function countOpDruk(collectors: Collector[]): number {
  return collectors.filter((c) => c.x_studio_collector_op_druk).length;
}

/** Count of collectors with uploaded-but-unapproved uitvoering photos. */
export function countFotosWachten(collectors: Collector[]): number {
  return collectors.filter((c) => c.x_studio_fotos_uitvoering === 'Fotos_Geupload').length;
}

/** Collectoren header: N totaal · X op druk · Y foto's open. */
export function collectorenSummary(collectors: Collector[]): ReactNode {
  const total = collectors.length;
  const opDruk = countOpDruk(collectors);
  const fotosOpen = countFotosWachten(collectors);
  return (
    <>
      <b className="font-extrabold text-td-navy">{total}</b> totaal ·{' '}
      <b className="font-extrabold text-td-navy">{opDruk}</b> op druk ·{' '}
      <b className="font-extrabold text-td-navy">{fotosOpen}</b> foto&apos;s open
    </>
  );
}

/**
 * The single shared systeem + legpatroon caption ("alle staalnet · LP 10 cm")
 * shown once above the clean collector list. Returns null when there is nothing
 * uniform to surface. Systeem is "alle {x}" when every collector shares one
 * system, otherwise "{n} systemen". Legpatroon is shown only when every kring
 * across all collectors carries the same non-zero legpatroon.
 */
export function collectorSharedCaption(collectors: Collector[]): {
  systeem: string | null;
  legpatroon: number | null;
} {
  const systems = new Set<string>();
  const patterns = new Set<number>();
  for (const c of collectors) {
    if (c.x_studio_collector_systeem) systems.add(c.x_studio_collector_systeem);
    for (const k of c.kringen ?? []) {
      if (k.x_legpatroon) patterns.add(k.x_legpatroon);
    }
  }
  const systeem =
    systems.size === 1 ? `alle ${[...systems][0]}` : systems.size > 1 ? `${systems.size} systemen` : null;
  const legpatroon = patterns.size === 1 ? [...patterns][0] : null;
  return { systeem, legpatroon };
}

/**
 * Materiaal header: "X van Y geleverd". Counts only outgoing leveringen
 * (`picking_type_code === 'outgoing'`); retours (incoming, werf → magazijn) are
 * excluded. A levering counts as geleverd once its picking reaches state 'done'.
 * Returns "geen leveringen" when the project has no outgoing pickings yet — so
 * the collapsed header never reads a bare title.
 */
export function materiaalSummary(pickings: StockPicking[]): ReactNode {
  const leveringen = pickings.filter((p) => p.picking_type_code === 'outgoing');
  if (leveringen.length === 0) return 'geen leveringen';
  const geleverd = leveringen.filter((p) => p.state === 'done').length;
  return (
    <>
      <b className="font-extrabold text-td-navy">{geleverd}</b> van{' '}
      <b className="font-extrabold text-td-navy">{leveringen.length}</b> geleverd
    </>
  );
}

/** Lifted vordering figures the section reports up for the collapsed header. */
export interface VorderingSummaryData {
  /** Contract netto (Σ all SOs, minprijs negative) — the vordering denominator. */
  contractNetto: number;
  /** Gefactureerd as a % of the contract netto, already capped at 100. */
  gefactureerdPct: number;
}

/** "€13.673 netto · 100% gefactureerd". Whole-euro, nl-BE thousands separator. */
export function vorderingSummary(data: VorderingSummaryData | null): ReactNode {
  if (!data) return null;
  const eur = new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 0 }).format(
    Math.round(data.contractNetto),
  );
  return (
    <>
      <b className="font-extrabold text-td-navy">€{eur}</b> netto ·{' '}
      <b className="font-extrabold text-td-navy">{Math.round(data.gefactureerdPct)}%</b> gefactureerd
    </>
  );
}

/** "N acties" — most-recent project activity count the widget reports up. */
export function activiteitSummary(count: number | null): ReactNode {
  if (count === null) return null;
  return (
    <>
      <b className="font-extrabold text-td-navy">{count}</b>{' '}
      {count === 1 ? 'actie' : 'acties'}
    </>
  );
}
